import { Device, until } from '@helpers/media';
import { withMotion } from '@hoc/withMotion';
import useMedia from '@hooks/useMedia';
import { FilterDropdownStyles } from '@stories/Elements/ProductFilters/FilterDropdown/FilterDropdown.styles';
import React, { FunctionComponent, useState } from 'react';

import { isSSR } from '@helpers/ssr';
import Help from '@img/icons/help-grey.svg';
import Close from '@img/icons/x.svg';
import M12 from '@img/logos/brand/M12.svg';
import M18 from '@img/logos/brand/M18.svg';
import M28 from '@img/logos/brand/M28.svg';
import MXFuel from '@img/logos/brand/MXFuel.svg';
import RedlithiumUSB from '@img/logos/brand/red-lithium-white.svg';
import OneKey from '@img/logos/brand/tech-onekey.svg';
import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useLockBodyScroll } from 'react-use';

export interface FilterDropdownProps {
  listLinks?: boolean;
  selectedValues?: string[];
  onChange?: (key: string, selectedValue: string[], replace?: boolean) => void;
  translations: Record<string, string | null>;
  viewModel: ProductFilterViewModel;
  activeViewModel?: ProductFilterViewModel;
  showAllButton: boolean;
  showCount: boolean;
  theme?: 'default' | 'border-accordion';
  activeByDefault?: boolean;
  disabled?: boolean;
}

const FilterDropdown: FunctionComponent<FilterDropdownProps> = ({
  listLinks,
  onChange,
  selectedValues,
  translations,
  viewModel: { filterOptions, title, key, tooltip },
  activeViewModel,
  showAllButton,
  showCount,
  theme = 'default',
  activeByDefault,
  disabled,
}) => {
  const [isActive, setIsActive] = useState<boolean>(
    activeByDefault
      ? true
      : theme === 'border-accordion'
      ? selectedValues
        ? selectedValues.length > 0
        : false
      : false,
  );
  const isMobile = useMedia([until(Device.TabletLarge)], [true], false);

  const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false);
  useLockBodyScroll(tooltipOpen);
  const position = React.useRef<DOMRect | undefined>(undefined);

  const emptyArray = !selectedValues || selectedValues.length === 0;

  const handleClick = async (): Promise<void> => {
    const selectedOptions = filterOptions?.map((x) => x.value);

    if (onChange) {
      if (emptyArray === true) {
        onChange(key, selectedOptions);
      } else {
        onChange(key, []);
      }
    }
  };

  const onRefChange = React.useCallback(
    (node: HTMLDivElement) => {
      if (node !== null && position.current) {
        // Get the position when it mounts, see if it's overflowing off the screen. //
        const bounds = node.getBoundingClientRect();
        if (bounds.y + bounds.height > window.innerHeight) {
          node.setAttribute('data-flip', 'true');

          const newStyle = node
            .getAttribute('style')
            ?.replace(/top: .+;/, `top: ${position.current.y - bounds.height - 15}px;`);
          if (newStyle) {
            node.setAttribute('style', newStyle);
          }
        } else {
          node.removeAttribute('data-flip');
        }
      }
    },
    [position.current],
  );

  const handleSelect = async (selectedOption: string): Promise<void> => {
    if (listLinks === true) {
      window.location.href = selectedOption;
      return;
    }

    if (onChange) {
      onChange(key, [selectedOption], key === 'showUnpublishedProducts' || key === 'perfectMatch');
    }
  };

  if (filterOptions.length === 0) {
    return null;
  }

  const getSystemLogoReplacement = (name: string): JSX.Element => {
    switch (name) {
      case 'M18':
        return <M18 />;
      case 'M12':
        return <M12 />;
      case 'M28':
        return <M28 />;
      case 'ONE-KEY™':
        return <OneKey />;
      case 'REDLITHIUM™ USB':
        return <RedlithiumUSB />;
      case 'MX FUEL':
        return <MXFuel />;
    }

    return <>{name}</>;
  };

  return (
    <>
      <FilterDropdownStyles.Dropdown
        disabled={disabled}
        aria-hidden={disabled ? 'true' : 'false'}
        data-cta-category="product-filter"
        data-theme={theme}
        data-active={isActive}
        onMouseEnter={() => {
          if (isMobile || theme === 'border-accordion') {
            return;
          }

          setIsActive(true);
        }}
        onMouseLeave={() => {
          if (isMobile || theme === 'border-accordion') {
            return;
          }

          setIsActive(false);
        }}
        onClick={() => setIsActive(theme === 'border-accordion' ? !isActive : (isMobile && !isActive) || !isMobile)}
        isActive={isActive}
      >
        <FilterDropdownStyles.Title>
          <FilterDropdownStyles.InnerContent>
            <span data-cta-text>{title}</span>
            {!!tooltip && (
              <FilterDropdownStyles.HelpIcon
                onClick={(e) => {
                  e.stopPropagation();
                  // First we have to retrieve the position before setting it to visible //
                  const pos = e.currentTarget.getBoundingClientRect();
                  position.current = pos;
                  setTooltipOpen(true);
                }}
              >
                <Help />
              </FilterDropdownStyles.HelpIcon>
            )}
          </FilterDropdownStyles.InnerContent>

          {showAllButton ? (
            <FilterDropdownStyles.Button
              isClear={emptyArray}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              {emptyArray === true ? translations['custom.all'] : translations['custom.clear']}
            </FilterDropdownStyles.Button>
          ) : null}
        </FilterDropdownStyles.Title>

        <FilterDropdownStyles.List data-theme={theme} isActive={isActive} onClick={(e) => e.stopPropagation()}>
          {filterOptions.map((x) => {
            const activeFilterOption = activeViewModel?.filterOptions.find((f) => f.value === x.value);

            if (listLinks) {
              return (
                <FilterDropdownStyles.Link key={x.value} href={x.value}>
                  <span data-cta-text>{x.name}</span>{' '}
                  {showCount && <small>({activeFilterOption ? activeFilterOption.count : 0})</small>}
                </FilterDropdownStyles.Link>
              );
            }

            return (
              <li key={x.value}>
                <FilterDropdownStyles.Item
                  data-cta-category="product-filter-option"
                  type="button"
                  onClick={() => {
                    if (!activeFilterOption) {
                      return;
                    }

                    handleSelect(x.value);
                  }}
                  isSelected={selectedValues?.includes(x.value)}
                  disabled={!activeFilterOption}
                >
                  <span data-cta-text={x.name}>{getSystemLogoReplacement(x.name)}</span>{' '}
                  {showCount && <small>({activeFilterOption ? activeFilterOption.count : 0})</small>}
                </FilterDropdownStyles.Item>
              </li>
            );
          })}
        </FilterDropdownStyles.List>
      </FilterDropdownStyles.Dropdown>
      {!isSSR && (
        <>
          {createPortal(
            <AnimatePresence>
              {tooltipOpen && (
                <FilterDropdownStyles.TooltipContainer onClick={() => setTooltipOpen(false)}>
                  {position.current && (
                    <FilterDropdownStyles.TooltipInner
                      ref={onRefChange}
                      data-right-offset={position.current.right}
                      onClick={(e) => e.stopPropagation()}
                      style={
                        isMobile
                          ? { left: 15, top: position.current.y + 40 }
                          : { left: position.current.x + (position.current.width + 20), top: position.current.y - 15 }
                      }
                    >
                      <Help />
                      <FilterDropdownStyles.TooltipContent>
                        {tooltip?.title && (
                          <FilterDropdownStyles.TooltipTitle
                            data-hide-margin={!tooltip.content}
                            dangerouslySetInnerHTML={{ __html: tooltip.title }}
                          />
                        )}
                        {tooltip?.content && (
                          <FilterDropdownStyles.TooltipInnerContent
                            dangerouslySetInnerHTML={{ __html: tooltip.content }}
                          />
                        )}
                      </FilterDropdownStyles.TooltipContent>
                      <FilterDropdownStyles.TooltipCloseContainer onClick={() => setTooltipOpen(false)}>
                        <Close />
                      </FilterDropdownStyles.TooltipCloseContainer>
                    </FilterDropdownStyles.TooltipInner>
                  )}
                </FilterDropdownStyles.TooltipContainer>
              )}
            </AnimatePresence>,
            document.body,
          )}
        </>
      )}
    </>
  );
};

export default withMotion(FilterDropdown);
